/**
 * Single source of truth for which files may be attached to a chat.
 *
 * Every attachment path funnels through `partitionFiles` — the paperclip
 * `<input type="file">` and the drag/drop handler in `useFileDrop` — so the
 * two can never drift apart on what they accept.
 */

/**
 * Types that get an inline image preview rather than a file chip. Chat pages
 * used to each keep their own copy of this list under the name `imageTypes`.
 */
export const IMAGE_MIME_TYPES = ['image/jpg', 'image/jpeg', 'image/png'];

export const ACCEPTED_ATTACHMENT_MIME_TYPES = [
  // images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  // documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // spreadsheets
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  // plain text
  'text/plain',
];

export const ACCEPTED_ATTACHMENT_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
];

/** Value for the `accept` attribute of an `<input type="file">`. */
export const ACCEPT_ATTR = ACCEPTED_ATTACHMENT_EXTENSIONS.join(',');

export type RejectReason = 'type' | 'count';

export interface RejectedFile {
  file: File;
  reason: RejectReason;
}

export interface FilePartition {
  accepted: File[];
  rejected: RejectedFile[];
}

export interface PartitionOptions {
  mimeTypes?: string[];
  extensions?: string[];
  /** Cap on files per batch. Omit for no limit. */
  maxFiles?: number;
}

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

export const isAcceptedFile = (
  file: File,
  mimeTypes: string[] = ACCEPTED_ATTACHMENT_MIME_TYPES,
  extensions: string[] = ACCEPTED_ATTACHMENT_EXTENSIONS
): boolean => {
  // Browsers disagree on the MIME type for CSV and Office files, and a drag
  // from some file managers hands us an empty `type`, so fall back to the
  // extension rather than rejecting a file the user legitimately picked.
  if (file.type && mimeTypes.indexOf(file.type.toLowerCase()) !== -1) return true;
  return extensions.indexOf(extensionOf(file.name)) !== -1;
};

export const partitionFiles = (files: File[], options: PartitionOptions = {}): FilePartition => {
  const { mimeTypes, extensions, maxFiles } = options;
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];

  files.forEach((file) => {
    if (!isAcceptedFile(file, mimeTypes, extensions)) {
      rejected.push({ file, reason: 'type' });
    } else if (typeof maxFiles === 'number' && accepted.length >= maxFiles) {
      rejected.push({ file, reason: 'count' });
    } else {
      accepted.push(file);
    }
  });

  return { accepted, rejected };
};
